import { useMemo } from 'react';

import { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type { UpstreamApiDashboardData } from 'src/types/dashboard';
import { getAggregateByQuestion } from 'src/components/dashboard/helper';

const useQuestionData = (
  comparisonType: DashboardWidgetComparisonType | undefined, // parameter must be passed in, but can be undefined
  question: string,
  data?: UpstreamApiDashboardData,
) => {
  const [dashboardResult, comparisonDataName] = useMemo(() => {
    const dashboardResult =
      data && comparisonType === DashboardWidgetComparisonType.ANCESTOR
        ? data.ancestor_result
        : data?.children_results[0];
    const comparisonDataName = dashboardResult?.org_units?.[0]?.name;
    return [dashboardResult, comparisonDataName];
  }, [data]);

  // Retrieve focalAggregate and extract important values
  const focalAggregate = useMemo(
    () => getAggregateByQuestion(question, data?.focal_result),
    [question, data],
  );

  // Retrieve comparisonAggregate and extract important values
  const comparisonAggregate = useMemo(
    () => getAggregateByQuestion(focalAggregate?.question, dashboardResult),
    [focalAggregate, dashboardResult],
  );

  /**
   * Chart data is an array of three values:
   * - pin1: Percentage of the first pin
   * - pin2: Percentage of the second pin
   * - The third value is calculated as 100 minus the sum of pin1 and pin2,
   * which represents the remaining percentage.
   */
  const chartData = useMemo(
    () =>
      focalAggregate
        ? [
            focalAggregate.pin1,
            focalAggregate.pin2,
            100 - focalAggregate.pin1 - focalAggregate.pin2,
          ]
        : [],
    [focalAggregate],
  );

  return {
    chartData,
    comparisonDataName,
    comparisonAggregate,
    focalAggregate,
  };
};

export default useQuestionData;
